import { useLayoutEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';

import { Alert } from '@interstate/components/Alert';
import { Typography } from '@interstate/components/Typography';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import * as configSelectors from '@makemydeal/dr-dash-store/selectors/config';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import { ContractSuccessAlert } from '@makemydeal/dr-dash-components';

import { DDJ_PRELOADER_ID } from '../constants';
import '../custom.d';
import { DDJWrapper } from './DDJRouteContainer.style';

const DigitalDealJacket = () => {
    const [error, setError] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const pbcElementRef = useRef<Element | null>(null);

    const isGenerateContractsEnabled = useSelector(dealerSelectors.getEnableGenerateContracts);
    const sourceUrl = useSelector(configSelectors.getDDJSourceUrl);
    const dealxgId = useSelector(offerReduxSelectors.getDealXgId);
    const dealerId = useSelector(dealerSelectors.getDealerId);

    // useLayoutEffect ensures cleanup runs synchronously BEFORE React removes DOM nodes,
    // allowing moveBefore to work since both nodes are still connected.
    useLayoutEffect(() => {
        const preloader = document.getElementById(DDJ_PRELOADER_ID);
        const preloaderStatus = preloader?.getAttribute('data-status');
        const preloadedPbc = preloader?.querySelector('ddj-app');
        let usedPreloader = false;

        if (preloaderStatus === 'error') {
            setError(true);
            logNewRelicPageAction('MV:DDJ - PBC Render Failure', {
                dealerId: String(dealerId),
                dealXgId: dealxgId,
                preloaderStatus
            });
            return;
        }

        if (preloadedPbc && containerRef.current) {
            pbcElementRef.current = preloadedPbc;
            const transferMethod = typeof containerRef.current.moveBefore === 'function' ? 'moveBefore' : 'appendChild';
            // Move the preloaded PBC from the hidden preloader to our visible container
            try {
                if (typeof containerRef.current.moveBefore === 'function') {
                    containerRef.current.moveBefore(preloadedPbc, null);
                } else {
                    containerRef.current.appendChild(preloadedPbc);
                }
                logNewRelicPageAction('MV:DDJ - DOM Transfer Success', {
                    dealerId: String(dealerId),
                    dealXgId: dealxgId,
                    method: transferMethod
                });
            } catch (err) {
                logNewRelicPageAction('MV:DDJ - DOM Transfer Failure', {
                    dealerId: String(dealerId),
                    dealXgId: dealxgId,
                    method: transferMethod,
                    error: err instanceof Error ? err.message : String(err)
                });
            }
            preloadedPbc.setAttribute('data-testid', 'digital-deal-jacket-pbc');
            usedPreloader = true;
        }

        return () => {
            if (usedPreloader) {
                // Move the PBC back to the preloader so it can be reused on next navigation
                const currentPreloader = document.getElementById(DDJ_PRELOADER_ID);
                const pbcElement = pbcElementRef.current;

                if (currentPreloader && pbcElement) {
                    pbcElement.setAttribute('data-testid', 'ddj-preloaded-pbc');
                    if (currentPreloader.moveBefore) {
                        currentPreloader.moveBefore(pbcElement, null);
                    } else {
                        currentPreloader.appendChild(pbcElement);
                    }
                }
                pbcElementRef.current = null;
            }
        };
    }, [sourceUrl]);

    return (
        <DDJWrapper>
            {error && (
                <Alert type="caution" sx={{ marginTop: '40px' }} data-testid="ddj-alert">
                    <Typography variant="h6" sx={{ marginBottom: '2px' }}>
                        An Error has Occurred
                    </Typography>
                    <Typography variant="body-sm" sx={{ marginBottom: '2px' }}>
                        Sorry, the system is currently unable to process your request.
                    </Typography>
                    <Typography variant="body-sm">Please try again later.</Typography>
                </Alert>
            )}
            {isGenerateContractsEnabled && <ContractSuccessAlert />}
            {!error && <div ref={containerRef} data-testid="ddj-pbc-container"></div>}
        </DDJWrapper>
    );
};

export default DigitalDealJacket;
